import { useEffect } from "react";
import { useBlocker } from "react-router-dom";

/**
 * Warns users when navigating away with unsaved changes.
 * Handles browser navigation (refresh/close) and in-app routing.
 *
 * @example
 * ```tsx
 * const blocker = useUnsavedChangesWarning(hasUnsavedChanges);
 * return <UnsavedChangesDialog blocker={blocker} />;
 * ```
 */
export function useUnsavedChangesWarning(hasUnsavedChanges: boolean) {
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    function handleBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return useBlocker(
    ({ currentLocation, nextLocation }) => hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
  );
}
